import { useListSpaces } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { Image as SpaceImage } from '@cocoonspace/sdk-js/types/image'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { Venue } from '@cocoonspace/sdk-js/types/venue'
import { TwicImg } from '@twicpics/components/react'
import { MapPin } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { Button } from '~/components/ui/button'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '~/components/ui/carousel'
import { Dialog, DialogContent, DialogTrigger } from '~/components/ui/dialog'
import { SpaceHoursContent } from '~/domains/spaces/components/space-details/space-hours'
import { useSpaceAddress } from '~/domains/spaces/hooks/use-space-address.hook'
import { useSpaceHours } from '~/domains/spaces/hooks/use-space-hours.hook'
import { TransportationAccessItems } from '../../molecules/transportation-access'

const FloorPlanDialog = ({ spaces }: { spaces?: Space[] }) => {
	const { t } = useTranslation()

	const venueFloorPlanImages = useMemo(
		() =>
			spaces
				?.reduce((acc, space) => {
					if (space.images.length) {
						acc.push(...space.images)
					}

					return acc
				}, [] as SpaceImage[])
				.filter((image) => image.type === 'floor_plan'),
		[spaces],
	)

	if (!venueFloorPlanImages?.length) return null

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button
					variant='outline'
					size='sm'
					className='rounded'
				>
					{t('venue:actions.seeFloorPlan.btnLabel')}
				</Button>
			</DialogTrigger>

			<DialogContent className='min-w-[90%]'>
				<Carousel className='mx-auto w-full max-w-screen-lg'>
					<CarouselContent>
						{venueFloorPlanImages?.map((image, index) => (
							<CarouselItem key={index}>
								<TwicImg
									src={image.path}
									ratio='3/2'
									preTransform='resize=1024x-/quality=80/focus=-x360'
								/>
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>
			</DialogContent>
		</Dialog>
	)
}

export const VenueInfo = ({ venue }: { venue: Venue }) => {
	const { t } = useTranslation()

	const spacesQuery = useListSpaces({ venues: [venue.id] })
	const spaces = spacesQuery.data?.data

	const { spaceHours: venueHours } = useSpaceHours(spaces?.[0]?.hours)
	const { fullAddress: venueAddress } = useSpaceAddress(spaces?.[0], true)

	return (
		<div className='flex flex-col gap-6 lg:col-span-2'>
			<div className='flex flex-wrap items-center justify-between gap-2'>
				<div>
					<h1 className='font-bold text-4xl'>{venue.name}</h1>

					<div className='flex items-center gap-x-1'>
						<MapPin className='size-4 shrink-0' />

						<span className='text-md leading-none'>{venueAddress}</span>
					</div>
				</div>

				<FloorPlanDialog spaces={spaces} />
			</div>

			{!!spaces?.[0]?.hours && (
				<div>
					<h2 className='font-semibold text-lg'>{t('venue:openingHours')}</h2>

					<div className='text-sm'>
						<SpaceHoursContent hours={venueHours} />
					</div>
				</div>
			)}

			{!!spaces?.length && (
				<div className='flex flex-col gap-2'>
					<h2 className='font-semibold text-lg'>
						{t('space:sections.location.nearest_stations')}
					</h2>

					<TransportationAccessItems
						space={spaces[0]}
						className='grids-cols-1 grid gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3'
					/>
				</div>
			)}
		</div>
	)
}
